/**
 * **A PDF list** — what one printout took: the donations a person printed
 * together, numbered per campaign, which a donation belongs to until the list
 * is voided or the donation is taken off it (docs/decisions/0092). A donation
 * is on **at most one active list**: printing it again is refused until its
 * list is voided or it is removed from it.
 */
export const PRINTOUT_LIST_STATUSES = ['active', 'voided'] as const;
export type PrintoutListStatus = (typeof PRINTOUT_LIST_STATUSES)[number];

/** How the list's donations were named: a selection, or the unlisted rest of a season's order. */
export const PRINTOUT_LIST_SOURCES = ['selection', 'order'] as const;
export type PrintoutListSource = (typeof PRINTOUT_LIST_SOURCES)[number];

/** Why a donation left a list: taken off it alone, or with the whole list voided. */
export const PRINTOUT_LIST_RELEASE_KINDS = ['removed', 'voided'] as const;
export type PrintoutListReleaseKind = (typeof PRINTOUT_LIST_RELEASE_KINDS)[number];

/** A void or a removal says why, at the deletion's bar — docs/decisions/0085. */
export const PRINTOUT_LIST_REASON_MIN_LENGTH = 10;
export const PRINTOUT_LIST_REASON_MAX_LENGTH = 500;

/** The reason as it is stored — trimmed — or null when it is too short or too long. */
export function printoutListReason(value: string | null | undefined): string | null {
  const trimmed = (value ?? '').trim();
  if (trimmed.length < PRINTOUT_LIST_REASON_MIN_LENGTH) return null;
  if (trimmed.length > PRINTOUT_LIST_REASON_MAX_LENGTH) return null;
  return trimmed;
}

/** The four scope columns of a scoped row, any of which a list may leave empty. */
export type ListPlacement = {
  regionId: string | null;
  zoneId: string | null;
  countryId: string | null;
  institutionId: string | null;
};

/**
 * **Where a list sits in the tree**: the deepest node every one of its
 * donations sits under, and nothing below it. A list of one institution's
 * donations names that institution and its ancestors; one spanning two
 * institutions of a country names the country and leaves the institution
 * empty; one spanning two regions names nothing, so only a global scope reads
 * it. A scope predicate over these columns therefore holds a list exactly when
 * it holds every donation on it — the list fails closed, as an organization
 * does (docs/decisions/0045).
 */
export function commonPlacement(placements: readonly ListPlacement[]): ListPlacement {
  const empty: ListPlacement = {
    regionId: null,
    zoneId: null,
    countryId: null,
    institutionId: null,
  };
  const [first] = placements;
  if (!first) return empty;
  const levels = ['regionId', 'zoneId', 'countryId', 'institutionId'] as const;
  const placement = { ...empty };
  for (const level of levels) {
    const value = first[level];
    if (value === null || placements.some((other) => other[level] !== value)) break;
    placement[level] = value;
  }
  return placement;
}

/** The one value every item shares, or null when they differ or there are none. */
export function sharedValue<T>(values: readonly T[]): T | null {
  const [first] = values;
  if (first === undefined) return null;
  return values.every((value) => value === first) ? first : null;
}
